import React, { useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const Analysis = () => {
  const { showToast } = useToast();
  const [selectedTool, setSelectedTool] = useState('trend');
  const [selectedDevice, setSelectedDevice] = useState(1);
  const [timeRange, setTimeRange] = useState('24h');

  const tools = [
    { id: 'trend', name: '趋势分析', icon: 'fas fa-chart-line' },
    { id: 'waveform', name: '波形分析', icon: 'fas fa-wave-square' },
    { id: 'spectrum', name: '频谱分析', icon: 'fas fa-chart-bar' },
    { id: 'multi', name: '多参量分析', icon: 'fas fa-chart-pie' }
  ];

  const devices = [
    { id: 1, name: '主电机 #1', data: '温度、振动、电流数据' },
    { id: 2, name: '减速机 #2', data: '温度、振动、油温数据' },
    { id: 3, name: '驱动滚筒 #3', data: '温度、振动、应力数据' }
  ];

  const timeRanges = [
    { id: '1h', name: '1小时' },
    { id: '24h', name: '24小时' },
    { id: '7d', name: '7天' }
  ];

  // 模拟图表数据
  const trendData = [
    { time: '00:00', temp: 62, vibration: 2.1, current: 150 },
    { time: '04:00', temp: 64, vibration: 2.2, current: 152 },
    { time: '08:00', temp: 66, vibration: 2.3, current: 154 },
    { time: '12:00', temp: 68, vibration: 2.4, current: 156 },
    { time: '16:00', temp: 65, vibration: 2.3, current: 155 },
    { time: '20:00', temp: 63, vibration: 2.2, current: 153 },
  ];

  const spectrumData = [
    { freq: '10Hz', amplitude: 0.2 },
    { freq: '25Hz', amplitude: 0.8 },
    { freq: '50Hz', amplitude: 1.0 },
    { freq: '75Hz', amplitude: 0.6 },
    { freq: '100Hz', amplitude: 0.4 },
    { freq: '125Hz', amplitude: 0.3 },
  ];

  const handleToolSelect = (toolId) => {
    setSelectedTool(toolId);
    const tool = tools.find(t => t.id === toolId);
    showToast(`已切换到${tool.name}模式`);
  };

  const handleTimeRangeSelect = (rangeId) => {
    setTimeRange(rangeId);
    const range = timeRanges.find(r => r.id === rangeId);
    showToast(`已选择${range.name}时间范围`);
  };

  const renderChart = () => {
    switch (selectedTool) {
      case 'trend':
        return (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="temp" stroke="#ef4444" strokeWidth={2} name="温度" />
              <Line type="monotone" dataKey="vibration" stroke="#3b82f6" strokeWidth={2} name="振动" />
              <Line type="monotone" dataKey="current" stroke="#10b981" strokeWidth={2} name="电流" />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'spectrum':
        return (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={spectrumData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="freq" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="amplitude" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        );
      default:
        return (
          <div className="h-48 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <i className="fas fa-chart-line text-3xl mb-3"></i>
              <div className="text-sm font-medium">图表区域</div>
              <div className="text-xs opacity-75">选择分析工具查看数据</div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="pb-4">
      {/* 顶部状态栏 */}
      <div className="phm-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">数据分析</h1>
            <p className="text-sm opacity-90">专业分析工具 · 多维度展示</p>
          </div>
          <div className="text-right">
            <div className="text-xs opacity-80">分析时间</div>
            <div className="text-sm font-semibold">最近24小时</div>
          </div>
        </div>
      </div>

      {/* 分析工具选择 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">分析工具</h3>
        <div className="grid grid-cols-2 gap-3">
          {tools.map((tool) => (
            <button
              key={tool.id}
              className={`flex items-center justify-center space-x-2 p-3 rounded-lg border transition-colors ${
                selectedTool === tool.id
                  ? 'bg-primary-50 text-primary-700 border-primary-200'
                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
              }`}
              onClick={() => handleToolSelect(tool.id)}
            >
              <i className={tool.icon}></i>
              <span className="text-sm font-medium">{tool.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 设备选择 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">选择分析设备</h3>
        <div className="space-y-2">
          {devices.map((device) => (
            <div
              key={device.id}
              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedDevice === device.id
                  ? 'bg-primary-50 border-primary-200'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
              onClick={() => setSelectedDevice(device.id)}
            >
              <div className="flex items-center space-x-3">
                <i className={`fas fa-cog ${selectedDevice === device.id ? 'text-primary-600' : 'text-gray-600'}`}></i>
                <div>
                  <div className="font-medium text-gray-800">{device.name}</div>
                  <div className="text-sm text-gray-500">{device.data}</div>
                </div>
              </div>
              <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                selectedDevice === device.id ? 'bg-primary-500' : 'bg-gray-300'
              }`}>
                {selectedDevice === device.id && (
                  <i className="fas fa-check text-white text-xs"></i>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 趋势分析图表 */}
      <div className="phm-chart-container">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800">温度趋势分析</h3>
          <div className="flex space-x-2">
            {timeRanges.map((range) => (
              <button
                key={range.id}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  timeRange === range.id
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => handleTimeRangeSelect(range.id)}
              >
                {range.name}
              </button>
            ))}
          </div>
        </div>
        {renderChart()}
      </div>

      {/* 多参量对比分析 */}
      <div className="phm-chart-container">
        <h3 className="font-semibold text-gray-800 mb-3">多参量对比分析</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">温度 (°C)</span>
            <div className="flex items-center space-x-2">
              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-red-500 rounded-full" style={{ width: '65%' }}></div>
              </div>
              <span className="text-sm font-medium text-gray-800">65°C</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">振动 (mm/s)</span>
            <div className="flex items-center space-x-2">
              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-yellow-500 rounded-full" style={{ width: '45%' }}></div>
              </div>
              <span className="text-sm font-medium text-gray-800">2.3mm/s</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">电流 (A)</span>
            <div className="flex items-center space-x-2">
              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full" style={{ width: '78%' }}></div>
              </div>
              <span className="text-sm font-medium text-gray-800">156A</span>
            </div>
          </div>
        </div>
      </div>

      {/* 分析结果 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">分析结果</h3>
        <div className="space-y-2">
          <div className="flex items-center space-x-3 p-2 bg-success-50 rounded-lg">
            <i className="fas fa-check-circle text-success-600"></i>
            <span className="text-sm text-gray-700">设备运行状态良好，各项参数在正常范围内</span>
          </div>
          <div className="flex items-center space-x-3 p-2 bg-warning-50 rounded-lg">
            <i className="fas fa-exclamation-triangle text-warning-600"></i>
            <span className="text-sm text-gray-700">减速机温度略高，建议关注润滑系统</span>
          </div>
          <div className="flex items-center space-x-3 p-2 bg-primary-50 rounded-lg">
            <i className="fas fa-info-circle text-primary-600"></i>
            <span className="text-sm text-gray-700">振动频谱显示正常，无异常频率成分</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analysis;
